import React from 'react';

import {
    getSquareOnlineStoreBrief,
    getSquareOnlineStoreBriefVariables,
} from '__generated__/getSquareOnlineStoreBrief';
import { SquareOnlineStoreBriefFields } from '__generated__/SquareOnlineStoreBriefFields';
import PartnerContext, { Partner } from 'projects/PartnerContext';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesignersPage from 'projects/routes/InviteDesignersPage';
import AboutYouPage from 'projects/routes/squareOnlineStore/AboutYouPage';
import DetailsPage from 'projects/routes/squareOnlineStore/DetailsPage';
import SummaryPage from 'projects/routes/squareOnlineStore/SummaryPage';
import TimeAndBudgetPage from 'projects/routes/squareOnlineStore/TimeAndBudgetPage';
import { Industries } from 'projects/util/industries';
import { BriefStep, StepState } from 'projects/util/step';
import { useRouteMatch } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import SquareAuthModal from './SquareAuthModal';
import SquareAuthModalDirect from './SquareAuthModalDirect';
import queries from './SquareOnlineStoreBrief.graphql';

interface RouteParams {
    briefId: string;
    step: string;
}

const steps: BriefStep<SquareOnlineStoreBriefFields>[] = [
    {
        step: 1,
        title: __('Tell us about your project'),
        fields: [
            'language',
            'title',
            'description',
            'projectType',
            'referenceFiles',
            'inspirationFiles',
            'inspirationUrls',
            'existingAssets',
        ],
    },
    {
        step: 2,
        title: __('Your time & budget'),
        fields: ['timeframe', 'budget', 'startDate', 'endDate'],
    },
    {
        step: 3,
        title: __('About you'),
        fields: ['industry', 'urls', 'profileApproval', 'other'],
    },
    { step: 4, title: __('Review your brief') },
    {
        step: 5,
        title: __('Invite designers to quote'),
        isAuthRequired: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

export default function SquareOnlineStoreBrief() {
    const route = useRouteMatch<RouteParams>();

    return (
        <PartnerContext.Provider value={{ partner: Partner.Square }}>
            <BriefQuery<getSquareOnlineStoreBrief, getSquareOnlineStoreBriefVariables>
                query={queries.getSquareOnlineStoreBrief}
                variables={{ id: route!.params.briefId }}
                fieldsTypename="SquareOnlineStoreBriefFields"
            >
                {(data, refetch) => {
                    const step = new StepState({
                        steps,
                        brief: data.brief,
                        currentStep: parseInt(route!.params.step, 10),
                    });

                    const brief = {
                        ...data.brief,
                        fields: data.brief.fields as SquareOnlineStoreBriefFields,
                    };

                    const industries = new Industries(data.industries);

                    return (
                        <Brief
                            refetch={refetch}
                            schema="squareOnlineStore"
                            brief={brief}
                            viewer={data.viewer}
                            stepState={step}
                            recommendedAuthModal={SquareAuthModal}
                            directAuthModal={SquareAuthModalDirect}
                        >
                            {form => (
                                <Switch>
                                    <Route
                                        path={`/briefs/square-online-store/:briefId/1`}
                                        render={() => <DetailsPage form={form} brief={brief} />}
                                    />
                                    <Route
                                        path={`/briefs/square-online-store/:briefId/2`}
                                        render={() => (
                                            <TimeAndBudgetPage form={form} brief={brief} />
                                        )}
                                    />
                                    <Route
                                        path={`/briefs/square-online-store/:briefId/3`}
                                        render={() => (
                                            <AboutYouPage
                                                form={form}
                                                industries={data.industries}
                                                brief={brief}
                                            />
                                        )}
                                    />
                                    <Route
                                        path={`/briefs/square-online-store/:briefId/4`}
                                        render={() => (
                                            <SummaryPage brief={brief} industries={industries} />
                                        )}
                                    />
                                    <Route
                                        path={`/briefs/square-online-store/:briefId/5`}
                                        render={() => (
                                            <InviteDesignersPage
                                                brief={brief}
                                                viewer={data.viewer}
                                                description={__(
                                                    'Here are the Square Online Store experts that best match your needs'
                                                )}
                                            />
                                        )}
                                    />
                                </Switch>
                            )}
                        </Brief>
                    );
                }}
            </BriefQuery>
        </PartnerContext.Provider>
    );
}
